<script setup>
import { computed, ref, watch } from 'vue'
import { useCart } from '@/frontend/vue/composables/useCart'

const props = defineProps({
    productId: { type: [Number, String], required: true },
    productName: { type: String, required: true },
    productHandle: { type: String, required: true },
    productImage: { type: String, default: null },
    productPrice: { type: [Number, String], default: 0 },
    productQuantity: { type: [Number, String], default: 0 },
    productSku: { type: String, default: null },
    isBrochureMode: { type: [Boolean, String], default: false },
    showPrices: { type: [Boolean, String], default: true },
    isInStock: { type: [Boolean, String], default: true },
    outOfStockMessage: { type: String, default: 'Out of Stock' },
    defaultImage: { type: String, default: '/images/default.jpg' },
})

const { cartItems, updateQuantity, formatPrice } = useCart()

const productId = computed(() => Number(props.productId))
const price = computed(() => Number(props.productPrice) || 0)
const maxQuantity = computed(() => Math.max(0, Number(props.productQuantity) || 0))

const isBrochureMode = computed(() => props.isBrochureMode === true || props.isBrochureMode === 'true')
const showPrices = computed(() => props.showPrices === true || props.showPrices === 'true')
const isInStock = computed(() => {
    const propInStock = props.isInStock === true || props.isInStock === 'true'
    return propInStock && maxQuantity.value > 0
})

const line = computed(() =>
    cartItems.value.find(item => item.id === productId.value && item.type === 'product') || null
)

const quantity = ref(line.value?.quantity || 0)

watch(
    line,
    newLine => {
        quantity.value = newLine?.quantity || 0
    },
    { immediate: true }
)

const canIncrease = computed(() => quantity.value < maxQuantity.value)
const canAdd = computed(() => quantity.value > 0)

function decreaseQuantity() {
    quantity.value = Math.max(0, quantity.value - 1)
}

function increaseQuantity() {
    if (canIncrease.value) {
        quantity.value += 1
    }
}

function enforceQuantityLimit() {
    quantity.value = Math.min(Math.max(0, Number(quantity.value) || 0), maxQuantity.value)
}

function applyCart() {
    updateQuantity({
        id: productId.value,
        type: 'product',
        name: props.productName,
        handle: props.productHandle,
        price: price.value,
        quantity: quantity.value,
        variant: null,
        code: props.productSku,
        image: props.productImage || props.defaultImage,
        max_quantity: maxQuantity.value,
    })
}
</script>

<template>
    <div class="product-main__nonstock-purchase">
        <template v-if="!isBrochureMode">
            <div v-if="isInStock">
                <div class="product-main__options table-wrapper">
                    <table class="table product-option-table">
                        <thead>
                            <tr>
                                <td class="product-option-table__label-price" v-if="showPrices">Price</td>
                                <td class="product-option-table__label-qty">Qty</td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td class="product-option-table__row-price" v-if="showPrices">
                                    {{ formatPrice(price) }}
                                </td>
                                <td class="quantity-control product-option-table__row-qty">
                                    <div class="quantity-container">
                                        <button type="button" class="number-decrease" @click="decreaseQuantity">-</button>
                                        <input
                                            type="number"
                                            min="0"
                                            :max="maxQuantity"
                                            step="1"
                                            v-model.number="quantity"
                                            @input="enforceQuantityLimit"
                                        />
                                        <button
                                            type="button"
                                            class="number-increase"
                                            :disabled="!canIncrease"
                                            @click="increaseQuantity"
                                        >+</button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <button
                    type="button"
                    class="btn btn-primary btn-lg btn-block btn-rounded"
                    :disabled="!canAdd"
                    @click="applyCart"
                >
                    {{ line ? 'Update Cart' : 'Add to Cart' }}
                </button>
            </div>

            <button v-else type="button" class="btn btn-primary btn-lg btn-block btn-rounded" disabled>
                {{ outOfStockMessage }}
            </button>
        </template>
    </div>
</template>
